/**
 * The shapes both halves of the app agree on.
 *
 * Identity and access came first (Stage 0); the item, its photos and the two
 * taxonomies it hangs from arrived with capture (Stage 1); modular fields with
 * a visibility, status, quantity, tags and search are Stage 2 (the "find"
 * section at the bottom). Sharing and listings are later, and nothing here
 * should guess at them.
 */

import type { InventoryKind } from "./stock.js";

/** Account states an admin can put someone in. Disabled is a door, not a delete. */
export type AccountStatus = "active" | "disabled";

/**
 * How somebody proved who they are. Only `email` (magic link) exists in Stage 0;
 * the other three are Stage 5 and are named here so the identities table, the
 * session payload and the admin console already have a vocabulary for them.
 */
export type AuthProvider = "email" | "passkey" | "google" | "microsoft";

/** An editor changes things; a viewer sees and exports but every write is refused. */
export type MemberRole = "owner" | "editor" | "viewer";

export const MEMBER_ROLE_LABELS: Record<MemberRole, string> = {
  owner: "Owner",
  editor: "Editor",
  viewer: "View only",
};

/**
 * One inventory an account can reach: its own, or one it was invited into.
 * `ownerEmail` is the label a switcher shows -- `inventories.name` is blank in
 * v1, and "whose stuff is this" is the fact that actually distinguishes them.
 */
export interface InventoryMembership {
  id: string;
  role: MemberRole;
  ownerEmail: string;
  /** Blank for the inventory made at signup; every inventory made since (Stage 7) is named. */
  name: string;
  kind: InventoryKind;
  /** The currency its amounts are written in, an ISO code from `CURRENCIES`. */
  currency: string;
  /**
   * When it was archived, or null. Archived is read-only and out of the
   * switcher, but still opens -- so it can be exported before it is deleted.
   */
  archivedAt: number | null;
}

/** What `GET /api/me` answers with when somebody is signed in. */
export interface SessionUser {
  id: string;
  email: string;
  name: string;
  status: AccountStatus;
  isAdmin: boolean;
  /** The inventory this account owns -- fixed at signup, never any other. */
  inventoryId: string;
  /**
   * The inventory being viewed right now: `inventoryId` by default, or one
   * from `memberships` that was switched to. Every inventory route resolves
   * this the same way, from the `X-Inventory-Id` header.
   */
  activeInventoryId: string;
  /** This account's role on the *active* inventory. */
  role: MemberRole;
  /** What kind of inventory the active one is. The stock screens exist only when this is `stock`. */
  activeKind: InventoryKind;
  /** The active inventory's currency, which every amount on screen is written in. */
  activeCurrency: string;
  /** The active inventory is archived: it can be read and exported, not changed. */
  activeArchived: boolean;
  /**
   * The stock add-on, per account: whether this account may make stock
   * inventories. Working in a stockroom somebody else shared needs no add-on
   * -- its owner has it, the way its owner's quota pays for its storage.
   */
  stockEnabled: boolean;
  /**
   * Storage for the active inventory, not necessarily this account's own --
   * storage is billed to whoever owns the inventory the bytes land in, so an
   * editor working in a shared inventory sees its owner's figures here rather
   * than their own unused quota. See docs/notes/decisions.md.
   */
  quotaBytes: number;
  usedBytes: number;
  /** Every inventory this account can reach, including its own. */
  memberships: InventoryMembership[];
}

/** One member of an inventory, as the sharing screen lists them. */
export interface InventoryMember {
  userId: string;
  email: string;
  role: MemberRole;
}

/* -------------------------------------------------------------- passkeys */

/**
 * A passkey, as the account screen lists it. Never the public key or the
 * counter -- those are the worker's business, for verifying a signature, not
 * anything a screen shows.
 */
export interface PasskeySummary {
  id: string;
  name: string;
  createdAt: number;
  lastUsedAt: number | null;
}

/**
 * The WebAuthn ceremony shapes that cross the wire, hand-written rather than
 * imported from `@simplewebauthn/server` -- that package is a `workers/api`
 * dependency only, and this package is bundled into the browser too. Each is
 * a deliberately narrow mirror of the shape the browser's own JSON
 * conversion produces and `@simplewebauthn/server` expects back, not a full
 * clone of the WebAuthn spec's types.
 */
export interface PasskeyCreationOptions {
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  challenge: string;
  pubKeyCredParams: { type: "public-key"; alg: number }[];
  timeout?: number;
  excludeCredentials?: { id: string; transports?: string[] }[];
  authenticatorSelection?: { residentKey?: string; userVerification?: string };
  attestation?: string;
}

/** What `navigator.credentials.create()` answers, converted to base64url for the wire. */
export interface PasskeyCreationResponse {
  id: string;
  rawId: string;
  type: "public-key";
  response: { clientDataJSON: string; attestationObject: string; transports?: string[] };
  clientExtensionResults: Record<string, never>;
}

export interface PasskeyRequestOptions {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: { id: string; transports?: string[] }[];
  userVerification?: string;
}

/** What `navigator.credentials.get()` answers, converted to base64url for the wire. */
export interface PasskeyRequestResponse {
  id: string;
  rawId: string;
  type: "public-key";
  response: { clientDataJSON: string; authenticatorData: string; signature: string; userHandle?: string };
  clientExtensionResults: Record<string, never>;
}

/** The inventory's own envelope, read out of its Durable Object. */
export interface InventoryMeta {
  inventoryId: string;
  /** The DO's internal SQLite schema version, so a stale object announces itself. */
  schemaVersion: number;
  createdAt: number;
  /** How many items it holds. Cheap, and it is what every screen asks first. */
  items: number;
}

/** One row of the signup allowlist. Plaintext addresses, by decision — see docs/notes/auth.md. */
export interface AllowlistEntry {
  email: string;
  note: string;
  addedBy: string;
  createdAt: number;
  /** Whether an account has actually been created for this address yet. */
  claimed: boolean;
}

/** What the admin console shows about an account. No inventory contents, ever. */
export interface AdminAccount {
  id: string;
  email: string;
  name: string;
  status: AccountStatus;
  quotaBytes: number;
  usedBytes: number;
  createdAt: number;
  /** The stock add-on. */
  stockEnabled: boolean;
  /** The inventories this account owns, home first. Names and counts only, never contents. */
  inventories: AdminInventory[];
}

/** An inventory as the admin console sees it: what it is called and who is in it, never what is in it. */
export interface AdminInventory {
  id: string;
  name: string;
  kind: InventoryKind;
  home: boolean;
  archivedAt: number | null;
  /** Everybody with a membership, the owner included. */
  members: number;
}

export interface AdminSummary {
  users: number;
  activeUsers: number;
  allowlisted: number;
  inventories: number;
  usedBytes: number;
}

/** What `/api/health` says about a deployment. `missing` names what is not configured. */
export interface HealthReport {
  ok: boolean;
  accounts: boolean;
  /** The mail binding. Absent, only magic-link sending is off. */
  email: boolean;
  /** The R2 bucket. Absent, the photo pipeline is off and everything else works. */
  photos: boolean;
  missing?: string[];
}

/** 2 GB, the owner-confirmed default. Admin-adjustable per account. */
export const DEFAULT_QUOTA_BYTES = 2 * 1024 * 1024 * 1024;

/* ------------------------------------------------------------------ items */

/**
 * Built-in and public by default, per the owner's locked decision. The empty
 * string is "not said yet", which is the state a photo-only item is saved in
 * and the state most items stay in until Stage 4 wants a listing.
 */
export type ItemCondition = "" | "new" | "like_new" | "good" | "fair" | "poor" | "for_parts";

export const ITEM_CONDITIONS: readonly ItemCondition[] = [
  "",
  "new",
  "like_new",
  "good",
  "fair",
  "poor",
  "for_parts",
];

/** The words a person reads, kept next to the values so a screen never invents its own. */
export const CONDITION_LABELS: Record<ItemCondition, string> = {
  "": "Not said",
  new: "New",
  like_new: "Like new",
  good: "Good",
  fair: "Fair",
  poor: "Poor",
  for_parts: "For parts",
};

/**
 * A photo, as everything outside the worker sees it.
 *
 * There is no R2 key here and there never should be: the key is the worker's
 * business, and a client that knew it would be one misconfigured bucket away
 * from being able to fetch it directly. Photos are addressed by id, through
 * `/api/photos/:id`, which checks the session first.
 */
export interface PhotoMeta {
  id: string;
  itemId: string;
  width: number;
  height: number;
  bytes: number;
  /** Ascending. The lowest is the cover; there is no separate flag to disagree with it. */
  sortOrder: number;
  createdAt: number;
}

/**
 * Who last touched an item, and when. Comes from a D1 side table
 * (`item_touches`), never from the Durable Object -- `InventoryStore` does not
 * know who its people are, on purpose (see docs/notes/data-layer.md). The
 * route fills this in around a store call the same way it already fills in
 * `usedBytes`; nothing inside the store ever sets it.
 */
export interface ItemTouch {
  email: string;
  name: string;
  at: number;
}

/** One item, whole. What the item screen shows and what `PATCH` answers with. */
export interface Item {
  id: string;
  title: string;
  description: string;
  notes: string;
  locationId: string | null;
  categoryId: string | null;
  /** Integer cents, or null for "no price yet". Never a float — see docs/notes/decisions.md. */
  priceCents: number | null;
  condition: ItemCondition;
  status: ItemStatus;
  quantity: number;
  /** Modular fields, in the order they were arranged. Each says whether it is public or private. */
  fields: ItemField[];
  /** Tag names, alphabetical. Many per item; the category is the single one. */
  tags: string[];
  createdAt: number;
  updatedAt: number;
  photos: PhotoMeta[];
  /**
   * Set by the route on every response that carries a whole item, null for an
   * item nobody has recorded a write for since Stage 5 (one captured earlier,
   * never touched again). Undefined only inside the Durable Object itself,
   * which never sets this field -- see `ItemTouch`.
   */
  updatedBy?: ItemTouch | null;
}

/**
 * What a card needs, and nothing else. A grid of a hundred of these should not
 * carry a hundred descriptions nobody is reading.
 */
export interface ItemSummary {
  id: string;
  title: string;
  locationId: string | null;
  categoryId: string | null;
  /** The cover photo's id, for `/api/photos/:id/thumb`. Null for an item with no photos. */
  coverPhotoId: string | null;
  photoCount: number;
  /** True while the item has no title: what the "needs details" filter looks for. */
  needsDetails: boolean;
  status: ItemStatus;
  quantity: number;
  /** Present only on search results: why this item matched, ready to render. */
  match?: SearchMatch;
  createdAt: number;
  updatedAt: number;
}

/** Everything a capture can say about a new item. All of it optional: photos alone are a valid item. */
export interface ItemDraft {
  title?: string;
  description?: string;
  notes?: string;
  locationId?: string | null;
  categoryId?: string | null;
  priceCents?: number | null;
  condition?: ItemCondition;
  status?: ItemStatus;
  quantity?: number;
  /** When present, the item's fields become exactly this list. Absent leaves them alone. */
  fields?: FieldInput[];
  /** When present, the item's tags become exactly this list. Absent leaves them alone. */
  tags?: string[];
}

/** A patch is a draft where absent means "leave it alone", which is why every field is optional. */
export type ItemPatch = ItemDraft;

/** What the browse screens ask for. Absent means unfiltered. */
/**
 * Where an item is as stock, as a filter: units on the shelf, units held in a
 * reserve, units out at a client, retired, or never put on the shelf at all.
 * A bulk line can be several of these at once -- 40 on the shelf, 12 held, 3
 * out -- and matches each of them.
 */
export type StockFilter = "shelf" | "held" | "out" | "retired" | "none";

export const STOCK_FILTERS: readonly StockFilter[] = ["shelf", "held", "out", "retired", "none"];

export const STOCK_FILTER_LABELS: Record<StockFilter, string> = {
  shelf: "On the shelf",
  held: "Held in a reserve",
  out: "Out at a client",
  retired: "Retired",
  none: "Not stock yet",
};

export const normaliseStockFilter = (value: unknown): StockFilter | undefined =>
  typeof value === "string" && (STOCK_FILTERS as readonly string[]).includes(value)
    ? (value as StockFilter)
    : undefined;

/** Whether an item has a photo yet: the pile somebody works through with a camera. */
export type PhotoFilter = "with" | "without";

export const PHOTO_FILTERS: readonly PhotoFilter[] = ["with", "without"];

export const PHOTO_FILTER_LABELS: Record<PhotoFilter, string> = {
  with: "Has a photo",
  without: "No photo",
};

export const normalisePhotoFilter = (value: unknown): PhotoFilter | undefined =>
  typeof value === "string" && (PHOTO_FILTERS as readonly string[]).includes(value)
    ? (value as PhotoFilter)
    : undefined;

export interface ItemQuery {
  locationId?: string | null;
  categoryId?: string | null;
  /** Only items still missing a title, for the clean-up pass after a fast sitting. */
  needsDetails?: boolean;
  /** Only items with at least one photo, or only those with none. */
  photos?: PhotoFilter;
  /** Free text. Present, the page is a search: ranked, with a `match` on every item. */
  q?: string;
  status?: ItemStatus;
  tagId?: string;
  /**
   * With `locationId`: include everything below it, not just what is filed
   * directly there. Search wants this ("Basement" should find the bin three
   * levels down); the shelf view does not.
   */
  deepLocation?: boolean;
  /** A stockroom's filter: where an item is as stock. Meaningless, and never sent, in a personal inventory. */
  stock?: StockFilter;
  /** A stockroom's filter: out at this client now. */
  clientId?: string;
  /**
   * Retired stock is out of every list unless this is set or `stock` is
   * `retired`: a laptop that was shredded is not something you own any more,
   * but the backup still has to carry it and its certificate.
   */
  withRetired?: boolean;
  sort?: ItemSort;
  /** Opaque, from the previous page's `nextCursor`. */
  cursor?: string | null;
  limit?: number;
}

export interface ItemPage {
  items: ItemSummary[];
  /** How many match the filter in total, so a screen can say "42 items" without walking them. */
  total: number;
  /** Null when this was the last page. */
  nextCursor: string | null;
}

/** How many items one page of a grid holds. Small enough to feel instant on a phone. */
export const ITEM_PAGE_SIZE = 24;

/* ------------------------------------------------------- where and what -- */

/** A node of the location tree: Basement > Shelf A > Bin 3. */
export interface Location {
  id: string;
  name: string;
  parentId: string | null;
  createdAt: number;
}

/** A location plus how many items sit in it directly. Descendants are rolled up client-side. */
export interface LocationCount extends Location {
  itemCount: number;
}

/** One category per item, by the brief. Tags (many per item) come with search. */
export interface Category {
  id: string;
  name: string;
  createdAt: number;
}

export interface CategoryCount extends Category {
  itemCount: number;
}

/* ----------------------------------------------------------------- photos */

/**
 * The photo pipeline's numbers, in one place because both sides need them: the
 * browser resizes to them and the worker refuses anything that ignored them.
 *
 * 2048px at quality 0.85 is the brief's assumption and it is a listing-usable
 * photo, around 1-1.5 MB. The thumbnail is what a grid of a hundred cards
 * actually loads.
 */
export const PHOTO_LONG_EDGE = 2048;
export const THUMB_LONG_EDGE = 400;
export const PHOTO_QUALITY = 0.85;
export const THUMB_QUALITY = 0.72;

/**
 * The worker's ceiling on one upload. Generous against a 2048px JPEG and still
 * small enough that a loop cannot fill a bucket quickly. A photo that arrives
 * larger than this was not resized by the app, which is the case worth refusing.
 */
export const MAX_PHOTO_BYTES = 8 * 1024 * 1024;
export const MAX_THUMB_BYTES = 512 * 1024;

/** How many photos one item can hold. The brief expects 1-3; this is a guard, not a target. */
export const MAX_PHOTOS_PER_ITEM = 12;

/* ------------------------------------------------------------- find (2) -- */

/**
 * Where an item stands. Built in rather than modular because the browse
 * screens, the search filters and (Stage 4) the listing flow all switch on it.
 */
export type ItemStatus = "keep" | "for_sale" | "sold" | "discarded";

export const ITEM_STATUSES: readonly ItemStatus[] = ["keep", "for_sale", "sold", "discarded"];

export const STATUS_LABELS: Record<ItemStatus, string> = {
  keep: "Keep",
  for_sale: "For sale",
  sold: "Sold",
  discarded: "Discarded",
};

/** More than this is a typo, and the bound is what keeps a `number` input honest. */
export const MAX_QUANTITY = 9999;

/**
 * What a modular field holds. Deliberately few: the type only changes how the
 * value is entered and shown, and every value is stored -- and searched -- as
 * text. A `url` is a web address the item screen offers as a link once saved.
 */
export type FieldType = "text" | "number" | "date" | "url";

export const FIELD_TYPES: readonly FieldType[] = ["text", "number", "date", "url"];

export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: "Text",
  number: "Number",
  date: "Date",
  url: "Link",
};

/**
 * The address a link field opens, or null when it is not one worth opening.
 * Only `http:` and `https:` ever come back: a value is typed by anybody with
 * edit access, and `javascript:` in an href is a script run by whoever clicks
 * it. A bare "example.com/manual" is taken to mean https.
 */
export function linkHref(value: string): string | null {
  const text = value.trim();
  if (!text || /[\s<>"]/.test(text)) return null;
  // Written without `URL`, which this package cannot assume: it builds for the
  // worker and the browser alike, with neither's lib.
  const scheme = text.match(/^([a-z][a-z0-9+.-]*):/i)?.[1]?.toLowerCase();
  // "localhost:8787" reads as a scheme to that regex; a port after it does not make one.
  const bare = scheme === undefined || /^[a-z0-9.-]+:\d+(?:[/?#]|$)/i.test(text);
  if (!bare && scheme !== "http" && scheme !== "https") return null;
  const href = bare ? `https://${text}` : text;
  const host = href.match(/^https?:\/\/(?:[^/?#@]*@)?([^/?#:]+)/i)?.[1]?.toLowerCase() ?? "";
  // "foo" alone is not an address anybody meant; a real one has a dot, or is localhost.
  if (!host || (!host.includes(".") && host !== "localhost")) return null;
  if (host.startsWith(".") || host.endsWith(".") || host.includes("..")) return null;
  return href;
}

/**
 * Chosen per field, on every field. This is the foundation Stage 3's prompt
 * builder and Stage 4's listing export stand on: each takes `publicFields()`
 * and nothing else, so a value marked private cannot reach a prompt or a
 * listing without somebody writing code that goes around it.
 */
export type FieldVisibility = "public" | "private";

/** One field on one item, as read back. */
export interface ItemField {
  id: string;
  label: string;
  value: string;
  type: FieldType;
  /** A heading to gather fields under ("Purchase", "Dimensions"). Empty means ungrouped. */
  group: string;
  visibility: FieldVisibility;
}

/** One field as written. The id is the server's business, so a write never carries one. */
export interface FieldInput {
  label: string;
  value: string;
  type?: FieldType;
  group?: string;
  visibility?: FieldVisibility;
}

/**
 * A field somebody expects to use again: "Serial number", "Purchase date".
 * Picking one adds an empty field with the label, type, group and visibility
 * already set, which is most of the work of describing the fortieth drill.
 */
export interface FieldTemplate {
  id: string;
  label: string;
  type: FieldType;
  group: string;
  visibility: FieldVisibility;
  createdAt: number;
}

export interface TagCount {
  id: string;
  name: string;
  itemCount: number;
  /** A stockroom's reserve tag: reserves match on it, and it is shown with a tag icon. */
  reserve?: boolean;
}

export const MAX_FIELDS_PER_ITEM = 40;
export const MAX_TAGS_PER_ITEM = 20;
export const MAX_FIELD_LABEL_LENGTH = 60;
export const MAX_FIELD_VALUE_LENGTH = 1000;

/** How a result list is ordered. `relevance` only means something with a query. */
export type ItemSort = "relevance" | "newest" | "oldest" | "updated" | "title";

export const ITEM_SORTS: readonly ItemSort[] = ["relevance", "newest", "oldest", "updated", "title"];

export const SORT_LABELS: Record<ItemSort, string> = {
  relevance: "Best match",
  newest: "Newest first",
  oldest: "Oldest first",
  updated: "Recently changed",
  title: "Title A to Z",
};

/**
 * A run of text and whether it is what the search matched. Highlighting is data
 * rather than markup so that no screen ever renders a search result as HTML --
 * the text in it is whatever somebody typed into a notes box.
 */
export interface TextPart {
  text: string;
  hit: boolean;
}

/** Why an item is in the results: the title, marked up, and the best excerpt from anywhere else. */
export interface SearchMatch {
  title: TextPart[];
  /** Which part of the item the excerpt is from ("Notes", "Serial number"...), or null if only the title matched. */
  where: string | null;
  excerpt: TextPart[];
}

/** A search somebody wants back. `query` is the search screen's own query string, so restoring it is a navigation. */
export interface SavedSearch {
  id: string;
  name: string;
  query: string;
  createdAt: number;
}

export const MAX_SAVED_SEARCHES = 30;
export const MAX_SAVED_QUERY_LENGTH = 500;

/**
 * The fields that may leave the private side: the only thing a prompt or a
 * listing is ever allowed to be built from. Kept next to the type so "which
 * fields are public" has exactly one implementation.
 */
export const publicFields = (fields: readonly ItemField[]): ItemField[] =>
  fields.filter((field) => field.visibility === "public");

/* ----------------------------------------------------------- report (6) -- */

/**
 * One category's slice of what is currently held (`keep` or `for_sale`).
 * `categoryId` is null for items with no category -- not a real category, so
 * it never comes from `listCategories()`, only from `report()` -- and the
 * screen labels it "No category" the same way `CategoryFilter` does.
 */
export interface CategoryValue {
  categoryId: string | null;
  categoryName: string;
  itemCount: number;
  valueCents: number;
}

/**
 * One status's items and what they were priced at. Covers all four statuses,
 * not only sold and kept, because comparing them is the point.
 */
export interface StatusValue {
  status: ItemStatus;
  itemCount: number;
  valueCents: number;
}

/**
 * Stage 6's report: what the inventory is estimated to be worth right now, by
 * category, and where every item stands by status. "Estimated" because it is
 * only as good as the price typed on each item -- there is no appraisal
 * behind it, which is what `unpricedItems` is for.
 *
 * `totalValueCents` and `byCategory` are both scoped to items currently held
 * (`keep` or `for_sale`), so the categories sum to the total. `byStatus` is
 * not scoped that way on purpose -- it exists to compare across statuses, so
 * it covers what was sold and what was discarded too.
 */
export interface InventoryReport {
  pricedItems: number;
  unpricedItems: number;
  totalValueCents: number;
  byCategory: CategoryValue[];
  byStatus: StatusValue[];
}
